import { createContext, useContext, useReducer, useCallback, useMemo, useEffect, useRef, useState } from 'react';

interface ChessTimerState {
  whiteElapsed: number; // in seconds - elapsed time counting UP
  blackElapsed: number; // in seconds - elapsed time counting UP
  activePlayer: 'white' | 'black';
  isRunning: boolean;
}

interface ChessTimerSettings {
  defaultWhiteTime: number;
  defaultBlackTime: number;
}

interface ChessTimerContextType {
  state: ChessTimerState;
  settings: ChessTimerSettings;
  startTimer: () => void;
  pauseTimer: () => void;
  resetTimer: () => void;
  flipTimer: () => void;
  setDefaultWhiteTime: (time: number) => void;
  setDefaultBlackTime: (time: number) => void;
}

const ChessTimerContext = createContext<ChessTimerContextType | null>(null);

const DEFAULT_SETTINGS: ChessTimerSettings = {
  defaultWhiteTime: 600, // 10 minutes
  defaultBlackTime: 600, // 10 minutes
};

const DEFAULT_STATE: ChessTimerState = {
  whiteElapsed: 0,
  blackElapsed: 0,
  activePlayer: 'white',
  isRunning: false,
};

type ChessTimerAction =
  | { type: 'SET_STATE'; payload: Partial<ChessTimerState> }
  | { type: 'TICK' }
  | { type: 'START' }
  | { type: 'PAUSE' }
  | { type: 'RESET' }
  | { type: 'FLIP' }
  | { type: 'UPDATE_SETTINGS'; payload: ChessTimerSettings };

function chessTimerReducer(state: ChessTimerState, action: ChessTimerAction): ChessTimerState {
  switch (action.type) {
    case 'SET_STATE':
      return { ...state, ...action.payload };

    case 'TICK':
      // Count UP by adding 1 second to active player's elapsed time
      if (state.isRunning) {
        const newState = { ...state };
        if (state.activePlayer === 'white') {
          newState.whiteElapsed += 1;
        } else {
          newState.blackElapsed += 1;
        }
        return newState;
      }
      return state;

    case 'START':
      return { ...state, isRunning: true };

    case 'PAUSE':
      return { ...state, isRunning: false };

    case 'RESET':
      return {
        ...state,
        whiteElapsed: 0,
        blackElapsed: 0,
        isRunning: false,
      };

    case 'FLIP':
      return {
        ...state,
        activePlayer: state.activePlayer === 'white' ? 'black' : 'white',
      };

    case 'UPDATE_SETTINGS':
      // Don't reset times when settings change, just save the new defaults
      return state;

    default:
      return state;
  }
}

export function ChessTimerProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(chessTimerReducer, DEFAULT_STATE);
  const settingsRef = useRef(DEFAULT_SETTINGS);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const tickIntervalRef = useRef<number | null>(null);
  const saveTimeoutRef = useRef<number | null>(null);

  // Load state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('chessTimerState');
    const savedSettings = localStorage.getItem('chessTimerSettings');

    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.whiteElapsed !== undefined && parsed.blackElapsed !== undefined) {
          dispatch({
            type: 'SET_STATE',
            payload: {
              whiteElapsed: parsed.whiteElapsed,
              blackElapsed: parsed.blackElapsed,
              activePlayer: parsed.activePlayer || 'white',
              isRunning: false, // Don't restore running state
            },
          });
        }
      } catch (error) {
        console.error('Failed to parse chess timer state:', error);
      }
    }

    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        if (parsed.defaultWhiteTime !== undefined && parsed.defaultBlackTime !== undefined) {
          const newSettings = { ...DEFAULT_SETTINGS, ...parsed };
          settingsRef.current = newSettings;
          setSettings(newSettings);
        }
      } catch (error) {
        console.error('Failed to parse chess timer settings:', error);
      }
    }
  }, []);

  // Save state to localStorage with debounce
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = window.setTimeout(() => {
      const stateToSave = {
        whiteElapsed: state.whiteElapsed,
        blackElapsed: state.blackElapsed,
        activePlayer: state.activePlayer,
        isRunning: false, // Don't save running state
      };
      localStorage.setItem('chessTimerState', JSON.stringify(stateToSave));
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [state.whiteElapsed, state.blackElapsed, state.activePlayer]);

  // Handle tick updates - count UP
  useEffect(() => {
    if (state.isRunning) {
      tickIntervalRef.current = window.setInterval(() => {
        dispatch({ type: 'TICK' });
      }, 1000);
    } else {
      if (tickIntervalRef.current) {
        window.clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
    }

    return () => {
      if (tickIntervalRef.current) {
        window.clearInterval(tickIntervalRef.current);
      }
    };
  }, [state.isRunning]);

  const startTimer = useCallback(() => {
    dispatch({ type: 'START' });
  }, []);

  const pauseTimer = useCallback(() => {
    dispatch({ type: 'PAUSE' });
  }, []);

  const resetTimer = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const flipTimer = useCallback(() => {
    dispatch({ type: 'FLIP' });
  }, []);

  const setDefaultWhiteTime = useCallback((time: number) => {
    const newSettings = { ...settings, defaultWhiteTime: time };
    settingsRef.current = newSettings;
    setSettings(newSettings);
    localStorage.setItem('chessTimerSettings', JSON.stringify(newSettings));
  }, [settings]);

  const setDefaultBlackTime = useCallback((time: number) => {
    const newSettings = { ...settings, defaultBlackTime: time };
    settingsRef.current = newSettings;
    setSettings(newSettings);
    localStorage.setItem('chessTimerSettings', JSON.stringify(newSettings));
  }, [settings]);

  const value = useMemo(() => ({
    state,
    settings,
    startTimer,
    pauseTimer,
    resetTimer,
    flipTimer,
    setDefaultWhiteTime,
    setDefaultBlackTime,
  }), [state, settings, startTimer, pauseTimer, resetTimer, flipTimer, setDefaultWhiteTime, setDefaultBlackTime]);

  return (
    <ChessTimerContext.Provider value={value}>
      {children}
    </ChessTimerContext.Provider>
  );
}

export function useChessTimer() {
  const context = useContext(ChessTimerContext);
  if (!context) {
    throw new Error('useChessTimer must be used within a ChessTimerProvider');
  }
  return context;
}
